import React from 'react'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { useToast } from '@wartek-id/toast'

import { PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { DialogPelaksanaanAppealRollback } from 'app/KinerjaPenilikPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealRollback'
import { DialogWarningPelaksanaanAppealRollback } from 'app/KinerjaPenilikPegawaiPage/components/DialogPelaksaanaanAppeal/DialogWarningPelaksanaanAppealRollback'
import { DialogPelaksanaanAppealRequest } from 'app/KinerjaPenilikPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealRequest'
import { DialogPelaksanaanAppealSubmitted } from 'app/KinerjaPenilikPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealSubmitted'
import { useAPIPostPraktikKinerjaRollbackState } from 'api/kinerja/penilik-pegawai/pelaksanaan/form'
import { usePelaksanaanPraktikKinerja } from 'app/KinerjaPenilikPegawaiPage/hooks/usePelaksanaanPraktikKinerja'
import { PELAKSANAAN_PRAKTIK_KINERJA_STATE_TITLE } from 'app/KinerjaPenilikPegawaiPage/constants/praktikKinerja'

import type { PraktikKinerjaFormName } from 'types/kinerja-penilik-pegawai'

const MSG_ERROR_SUBMIT = 'Gagal ubah menjadi draf. Coba lagi.'
const MSG_SUCCESS_REQUEST = 'Berhasil mengajukan perubahan!'

interface TButtonPelaksanaanAppealRollbackProps {
  formName: PraktikKinerjaFormName
}
export const ButtonPelaksanaanAppealRollback = ({
  formName,
}: TButtonPelaksanaanAppealRollbackProps) => {
  const router = useRouter()
  const toast = useToast()

  const { penilikSummaryHookResult, isNeedRollbackApproval } =
    usePelaksanaanPraktikKinerja({
      formName,
    })
  const { summary } = penilikSummaryHookResult
  const { mutateAsync } = useAPIPostPraktikKinerjaRollbackState({
    skpId: summary?.skpId ?? '',
  })

  const [isDialogAppealRollbackOpen, setDialogAppealRollbackOpen] =
    React.useState<boolean>(false)
  const [
    isWarningDialogAppealRollbackOpen,
    setWarningDialogAppealRollbackOpen,
  ] = React.useState<boolean>(false)
  const [isDialogAppealSubmittedOpen, setDialogAppealSubmittedOpen] =
    React.useState<boolean>(false)

  const handleRollback = async () => {
    try {
      await mutateAsync({
        formName,
      })

      if (isNeedRollbackApproval) {
        setDialogAppealSubmittedOpen(true)
      } else {
        router.reload()
      }
    } catch (err) {
      toast({ message: MSG_ERROR_SUBMIT })
    } finally {
      setDialogAppealRollbackOpen(false)
    }
  }

  const isAppealStateExist = React.useMemo(() => {
    return Boolean(summary?.pelaksanaanStatuses?.praktikKinerjaPerubahanStatus)
  }, [summary])

  const isPenetapanSentToEkin = React.useMemo(() => {
    return [
      'NOT_SENT',
      'SENT_TO_PPK',
      'SENDING_TO_BKN',
      'SENT_TO_BKN',
      'ERROR_SEND',
    ].includes(summary?.penetapanStatus)
  }, [summary?.penetapanStatus])

  const isDisabled = isAppealStateExist || isPenetapanSentToEkin

  return (
    <>
      <Button
        color={isDisabled ? 'black' : 'white'}
        className="flex items-center justify-center gap-x-2"
        onClick={() => {
          if (
            summary?.pelaksanaanStatuses?.praktikKinerja === 'SELESAI' &&
            formName === 'diskusi_persiapan'
          ) {
            setWarningDialogAppealRollbackOpen(true)
            return
          }
          setDialogAppealRollbackOpen(true)
        }}
        size="md"
        variant="solid"
        disabled={isDisabled}
      >
        <Icon fontSize="sm" color={isDisabled ? 'disabled' : 'default'}>
          lock_outline
        </Icon>
        <Text variant="action" color={isDisabled ? 'disabled' : 'default'}>
          Ubah isi {PELAKSANAAN_PRAKTIK_KINERJA_STATE_TITLE?.[formName]}
        </Text>
      </Button>

      {isWarningDialogAppealRollbackOpen && (
        <DialogWarningPelaksanaanAppealRollback
          isOpen
          isLoading={false}
          onClose={() => {
            setWarningDialogAppealRollbackOpen(false)
          }}
          onConfirm={() => {
            setWarningDialogAppealRollbackOpen(false)
            setTimeout(() => {
              setDialogAppealRollbackOpen(true)
            }, 50)
          }}
        />
      )}

      {isNeedRollbackApproval ? (
        <DialogPelaksanaanAppealRequest
          formName={formName}
          currentState={summary?.pelaksanaanStatuses?.praktikKinerja}
          isLoading={false}
          isOpen={isDialogAppealRollbackOpen}
          onClose={() => {
            setDialogAppealRollbackOpen(false)
          }}
          onConfirm={() => {
            handleRollback()
          }}
        />
      ) : (
        <DialogPelaksanaanAppealRollback
          isLoading={false}
          isOpen={isDialogAppealRollbackOpen}
          onClose={() => {
            setDialogAppealRollbackOpen(false)
          }}
          onConfirm={handleRollback}
        />
      )}

      <DialogPelaksanaanAppealSubmitted
        isOpen={isDialogAppealSubmittedOpen}
        onClose={async () => {
          setDialogAppealSubmittedOpen(false)
          await router.push(
            `${PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
          )
          toast({ message: MSG_SUCCESS_REQUEST, type: 'success' })
        }}
      />
    </>
  )
}
